/**
 * The email newsletter's editions — the vocabulary, limits and pure checks
 * shared by the edition actions, the send saga and the `/campaigns` page
 * (email-newsletter-editions, 2026-10-18;
 * build-output/architecture/email-campaigns.md → "Editions").
 *
 * An edition is written HTML-first, the way E-Pulsive's newsletters were
 * already made: the lane copies the current template and changes words,
 * images and links, one finished body per `lifecycle:goal`. Clavis fills
 * exactly three tokens at send time — `{{first_name}}`, `{{preview_text}}`
 * (the hidden preheader, per A/B arm) and `{{unsubscribe_url}}` — and
 * refuses a body that carries anything else, is too large, cites an image
 * outside the studio's own asset host, or links anywhere but https or
 * mailto ({@link checkNewsletterBody}). `upsert_newsletter_edition` applies
 * these checks; migration 233 bounds the rest of the row's shape.
 *
 * Pure, no runtime imports: the page bundles this file through a Vite alias.
 */

import {
  MARKETING_GOALS,
  MARKETING_GOAL_BODY_FALLBACK,
  MARKETING_LIFECYCLES,
  emptyExclusionCounts,
  type MarketingAudienceEntry,
  type MarketingExclusionReason,
  type MarketingGoal,
  type MarketingLifecycle,
} from "./marketing-audience.js";
import {
  countLinks,
  decodeHtmlReferences,
  normalizeUrlValue,
  scanHtml,
  srcsetUrls,
} from "./newsletter-html.js";

// ---------------------------------------------------------------------------
// Editions.
// ---------------------------------------------------------------------------

/**
 * An edition's slug: its send date, then a short topic
 * (`2026-10-29-fitness-peak`) — the token the lane's review emails carry as
 * `[NL-2026-10-29-fitness-peak vN]`.
 */
export const NEWSLETTER_EDITION_SLUG_PATTERN = "^[0-9]{4}-[0-9]{2}-[0-9]{2}-[a-z0-9]+(-[a-z0-9]+)*$";
export const NEWSLETTER_EDITION_SLUG_MAX = 80;

/**
 * Where an edition is (migration 233's CHECK):
 *   - `awaiting_approval` — version N saved, not yet approved;
 *   - `approved` — Mario (or an admin) approved exactly version N;
 *   - `scheduled` — the send froze its recipients and the saga sleeps until
 *     `scheduled_for`;
 *   - `sending` — batches are going out;
 *   - `sent` — every batch is settled;
 *   - `cancelled` — withdrawn before it went out.
 * A new version may be saved only while `awaiting_approval` or `approved`;
 * saving one clears the approval.
 */
export const NEWSLETTER_EDITION_STATUSES = [
  "awaiting_approval",
  "approved",
  "scheduled",
  "sending",
  "sent",
  "cancelled",
] as const;
export type NewsletterEditionStatus = (typeof NEWSLETTER_EDITION_STATUSES)[number];

/** The statuses in which a new version may still be saved. */
export const NEWSLETTER_EDITION_EDITABLE_STATUSES: ReadonlyArray<NewsletterEditionStatus> = [
  "awaiting_approval",
  "approved",
];

/**
 * How an approval was given: Mario's APPROVE reply through the newsletter lane,
 * an admin's press of Approve on /campaigns, or (migration 254, 2026-10-28) an
 * admin's own MCP connector calling `approve_newsletter_edition` under their
 * name.
 */
export const NEWSLETTER_APPROVAL_VIAS = ["email_reply", "dashboard", "connector"] as const;
export type NewsletterApprovalVia = (typeof NEWSLETTER_APPROVAL_VIAS)[number];

/** The two arms of an edition's A/B test. */
export const NEWSLETTER_VARIANTS = ["A", "B"] as const;
export type NewsletterVariant = (typeof NEWSLETTER_VARIANTS)[number];

/** The metric an experiment is decided on. */
export const NEWSLETTER_PRIMARY_METRICS = [
  "unique_open_rate",
  "unique_click_rate",
  "unsubscribe_rate",
] as const;
export type NewsletterPrimaryMetric = (typeof NEWSLETTER_PRIMARY_METRICS)[number];

/** The checkpoint an experiment is decided at. */
export const NEWSLETTER_DECISION_CHECKPOINTS = ["48h", "7d", "14d"] as const;
export type NewsletterDecisionCheckpoint = (typeof NEWSLETTER_DECISION_CHECKPOINTS)[number];

export const NEWSLETTER_TITLE_MAX = 120;
export const NEWSLETTER_TOPIC_MAX = 300;
export const NEWSLETTER_HOOK_MAX = 300;
export const NEWSLETTER_SUBJECT_MAX = 150;
export const NEWSLETTER_PREVIEW_TEXT_MAX = 200;
export const NEWSLETTER_HYPOTHESIS_MAX = 1000;
export const NEWSLETTER_VARIABLE_MAX = 200;
/** A display name in the From line (`E-Pulsive`). */
export const NEWSLETTER_FROM_NAME_MAX = 64;

/** One finished body, as UTF-8 bytes. */
export const NEWSLETTER_BODY_MAX_BYTES = 100_000;

/**
 * The claims list (every factual claim with its source) and the research
 * list (what the topic was drawn from): entries, and each field's length.
 */
export const NEWSLETTER_CLAIMS_MAX = 40;
export const NEWSLETTER_RESEARCH_MAX = 40;
export const NEWSLETTER_CLAIM_MAX = 1_000;
export const NEWSLETTER_SOURCE_TITLE_MAX = 300;
export const NEWSLETTER_NOTE_MAX = 1_000;
export const NEWSLETTER_URL_MAX = 2_000;

/** Bodies per edition: one per lifecycle × goal at most. */
export const NEWSLETTER_BODIES_MAX = 20;

/**
 * Tags an edition may be picked for (email-newsletter-tag-audience,
 * 2026-10-31): the WhatsApp newsletter's limit, and migration 269's CHECK.
 */
export const NEWSLETTER_AUDIENCE_TAGS_MAX = 20;

/** How far ahead a send may be scheduled. */
export const NEWSLETTER_SCHEDULE_MAX_DAYS = 14;

/**
 * Most people one edition may go to. Above E-Pulsive's ~2,500 reachable
 * members with room to grow; a larger list is a deliberate change here, not
 * a surprise.
 */
export const NEWSLETTER_MAX_RECIPIENTS = 5_000;

/** Recipients per Resend batch call — Resend's own maximum. */
export const NEWSLETTER_BATCH_SIZE = 100;

/**
 * The most body bytes one batch call should carry. Measured: 100 emails of
 * ~20 KB (2 MB) answered in 1.4 s (2026-10-19). Bodies may be up to 100 KB,
 * so a batch of the largest would be 10 MB; Resend documents no request cap,
 * so batches of larger bodies are made smaller instead of finding one.
 */
export const NEWSLETTER_BATCH_MAX_BYTES = 4_000_000;

/** Fewest recipients per batch, however large the bodies. */
export const NEWSLETTER_BATCH_MIN_SIZE = 10;

/**
 * Recipients per batch for an edition whose largest body is `maxBodyBytes`:
 * {@link NEWSLETTER_BATCH_SIZE}, or fewer so a batch stays near
 * {@link NEWSLETTER_BATCH_MAX_BYTES} (each email also carries ~4 KB of
 * subject, headers, tags and the filled tokens).
 */
export function newsletterBatchSize(maxBodyBytes: number): number {
  const perEmail = Math.max(0, maxBodyBytes) + 4_000;
  return Math.max(
    NEWSLETTER_BATCH_MIN_SIZE,
    Math.min(NEWSLETTER_BATCH_SIZE, Math.floor(NEWSLETTER_BATCH_MAX_BYTES / perEmail)),
  );
}

/** `outbound_messages.source` of a newsletter email (migration 235). */
export const NEWSLETTER_OUTBOUND_SOURCE = "email_newsletter";

/**
 * The Resend tags every newsletter email carries: its edition, campaign,
 * lifecycle and arm, readable on Resend's own dashboard and stored on each
 * webhook event (`resend_message_events.event_payload.tags`, as an object).
 * The tracker does not count by them — it joins each campaign's recipient
 * rows to the stored events by Resend id (`email-campaign-tracker-postgres.ts`),
 * so an email whose id was never read back (`unknown`) is not counted. Tag
 * values take only ASCII letters, digits, `_` and `-`: ids and keys qualify,
 * `lifecycle:goal` would not.
 */
export const NEWSLETTER_TAG_EDITION = "clavis_edition";
export const NEWSLETTER_TAG_CAMPAIGN = "clavis_campaign";
export const NEWSLETTER_TAG_LIFECYCLE = "clavis_lifecycle";
export const NEWSLETTER_TAG_VARIANT = "clavis_variant";

/**
 * One batch's Resend `Idempotency-Key` — the same for every attempt at that
 * batch, so a retry of an email Resend already took gets its id back instead
 * of a second copy (verified 2026-10-19, assumption test A1). It names the
 * send's run (`newsletter_editions.send_run_id`) as well as the edition: a
 * send released with nothing gone and scheduled again is a new run, whose
 * batches hold other recipients and campaign ids, and Resend, which keeps a
 * key for 24 hours, would refuse that changed payload under the old key (409
 * `invalid_idempotent_request`).
 */
export function newsletterBatchIdempotencyKey(editionId: string, runId: string, batchNo: number): string {
  return `clavis-nl-${editionId}-${runId}-b${String(batchNo)}`;
}

/** Where a recipient row stands. */
export const NEWSLETTER_RECIPIENT_STATUSES = ["pending", "sent", "skipped", "failed", "unknown"] as const;
export type NewsletterRecipientStatus = (typeof NEWSLETTER_RECIPIENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Bodies.
// ---------------------------------------------------------------------------

/** The three tokens Clavis fills at send time. */
export const NEWSLETTER_TOKENS = ["first_name", "preview_text", "unsubscribe_url"] as const;
export type NewsletterToken = (typeof NEWSLETTER_TOKENS)[number];

/** Tokens every body must carry. */
export const NEWSLETTER_REQUIRED_TOKENS: ReadonlyArray<NewsletterToken> = [
  "preview_text",
  "unsubscribe_url",
];

/** A body's key: `lifecycle:goal` (`member:general`, `lead:weight_loss`). */
export const NEWSLETTER_BODY_KEY_PATTERN =
  "^(member|trial|winback|lead):(rehab_injury|weight_loss|toning|muscle|general)$";

export function newsletterBodyKey(lifecycle: MarketingLifecycle, goal: MarketingGoal): string {
  return `${lifecycle}:${goal}`;
}

/**
 * The body a member gets: the one for their goal, else the goal's fallbacks
 * in order, ending at `<lifecycle>:general`. `null` only when the edition
 * has no body for the lifecycle at all — which {@link checkNewsletterBodies}
 * refuses for any lifecycle the edition sends to.
 */
export function pickNewsletterBodyKey(
  bodyKeys: ReadonlySet<string>,
  lifecycle: MarketingLifecycle,
  goal: MarketingGoal,
): string | null {
  for (const g of [goal, ...MARKETING_GOAL_BODY_FALLBACK[goal]]) {
    const key = newsletterBodyKey(lifecycle, g);
    if (bodyKeys.has(key)) return key;
  }
  return null;
}

/** One problem found in a body. */
export interface NewsletterBodyProblem {
  readonly body: string;
  readonly problem: string;
}

/**
 * A token: `{{name}}`, spaces allowed inside the braces. No two parts of it
 * can match the same characters, so a long run of `{{` and spaces is read in
 * linear time (the first pattern, `\s*` either side of an optional name,
 * backtracked quadratically).
 */
const TOKEN_RE = /\{\{\s*(?:([a-zA-Z0-9_.-]+)\s*)?\}\}/gu;
const UNSUBSCRIBE_HREF_RE = /^\{\{\s*unsubscribe_url\s*\}\}$/u;

/**
 * The tags a body may use: the standard email-template set. Anything else —
 * scripts, frames, forms, SVG, media, `<link>`, `<base>` — is refused, so a
 * new kind of markup is a deliberate change here rather than a surprise.
 */
const NEWSLETTER_TAGS: ReadonlySet<string> = new Set([
  // The document.
  "html", "head", "meta", "title", "style", "body",
  // Layout.
  "table", "thead", "tbody", "tfoot", "tr", "td", "th", "caption", "colgroup", "col",
  "div", "span", "p", "br", "hr", "center",
  "section", "header", "footer", "article", "main", "aside", "figure", "figcaption", "address",
  // Text, links and pictures.
  "h1", "h2", "h3", "h4", "h5", "h6",
  "a", "img", "strong", "b", "em", "i", "u", "s", "small", "big", "sup", "sub", "font",
  "strike", "del", "ins", "mark", "abbr", "cite", "q", "blockquote", "pre", "code", "time", "wbr",
  "ul", "ol", "li", "dl", "dt", "dd",
]);

/**
 * Outlook's own markup — VML shapes (`<v:roundrect>`), Office settings
 * (`<o:OfficeDocumentSettings>`) — allowed only inside a comment, which is
 * where Outlook's conditional comments put it.
 */
const isOutlookTag = (name: string): boolean => name === "xml" || /^[vow]:[a-z]/u.test(name);

/** Attributes that make a mail client load a picture. */
const IMAGE_ATTRIBUTES: ReadonlySet<string> = new Set([
  "src",
  "srcset",
  "background",
  "poster",
  "lowsrc",
  "dynsrc",
]);

/**
 * Attributes that take an address a newsletter has no use for: a form
 * target, a ping, a plugin, a profile, an XLink. (`href` is checked as a
 * link, the attributes above as pictures.)
 */
const OTHER_URL_ATTRIBUTES: ReadonlySet<string> = new Set([
  "action",
  "formaction",
  "ping",
  "cite",
  "longdesc",
  "codebase",
  "manifest",
  "icon",
  "usemap",
  "archive",
  "classid",
  "profile",
  "data",
  "srcdoc",
  "xlink:href",
  "xml:base",
]);

/**
 * The `<meta http-equiv>` values a template carries. Any other — a
 * `refresh` redirect, `set-cookie`, a policy — is refused.
 */
const META_HTTP_EQUIV: ReadonlySet<string> = new Set(["content-type", "x-ua-compatible"]);

/**
 * Every problem with one piece of CSS — a `<style>` block, or a `style`
 * attribute with its character references decoded: an escape (`\`), which
 * can spell any of the rest in disguise; another stylesheet; a script-capable
 * property or function; a picture loaded without `url()`; a `url()` outside
 * the studio's media host. `fromAssets` says whether an address is on it.
 */
function cssProblems(
  css: string,
  where: string,
  assetPrefix: string,
  fromAssets: (url: string) => boolean,
): string[] {
  const out: string[] = [];
  if (css.includes("\\")) out.push(`has a CSS escape (\\) in ${where}`);
  if (/@import/iu.test(css)) out.push(`imports another stylesheet in ${where}`);
  if (/expression\s*\(|(?:^|[^a-z0-9_-])(?:behavior|-moz-binding)\s*:/iu.test(css)) {
    out.push(`has script-capable CSS in ${where}`);
  }
  if (/image-set\s*\(|(?:^|[^a-z0-9_-])(?:image|src)\s*\(/iu.test(css)) {
    out.push(`loads a picture without url() in ${where}`);
  }
  for (const m of css.matchAll(/url\s*\(\s*["']?/giu)) {
    // A window, not the rest of the CSS: a body of many url()s stays linear.
    // Leading spaces and control characters are skipped by hand, as the URL
    // parser skips them (`normalizeUrlValue`).
    const end = Math.min(css.length, (m.index ?? 0) + m[0].length + 4096);
    let at = (m.index ?? 0) + m[0].length;
    while (at < end && css.charCodeAt(at) <= 0x20) at += 1;
    const rest = css.slice(at, end);
    if (!fromAssets(rest)) {
      const shown = /^[^"')\s]*/u.exec(rest)?.[0] ?? "";
      out.push(`shows an image from outside ${assetPrefix}: ${shown.slice(0, 120)}`);
    }
  }
  return out;
}

/**
 * Every problem with one finished body, read as a mail client reads it
 * (`./newsletter-html.ts`: every tag and attribute, quoted or not, with its
 * character references decoded; every `<style>` block; the markup inside
 * Outlook's conditional comments). A body is refused when it:
 *   - is over {@link NEWSLETTER_BODY_MAX_BYTES}, or has markup that cannot be
 *     read (an unclosed tag, quote or comment);
 *   - carries a token other than the three, a broken one, or lacks
 *     `{{preview_text}}` or a link to `{{unsubscribe_url}}` outside any
 *     comment with words or a picture inside it (whether CSS hides the link
 *     is not checked: the approver reads the body as a member sees it);
 *   - repeats an attribute in one tag: the HTML parser keeps the first and
 *     drops the rest, so refusing it means no reader is shown the other;
 *   - fills a token into an address or CSS (a name could rewrite either:
 *     the fill escapes HTML, not URLs or CSS), into an unquoted attribute,
 *     or where a tag name goes — `{{unsubscribe_url}}` is filled only as a
 *     whole `href`;
 *   - uses a tag outside the email-template set, an `on…` handler, or a
 *     `<meta http-equiv>` other than content-type and X-UA-Compatible;
 *   - shows a picture from anywhere but `assetsBaseUrl` — the studio's own
 *     media host (`studio.branding.assets_base_url`, no trailing slash) — so
 *     a recipient never loads one from a host the studio does not control;
 *   - links anywhere but https or mailto, or uses any other address
 *     attribute;
 *   - has CSS that escapes, imports, runs script or loads a picture other
 *     than through a `url()` on the media host.
 * Each problem is reported once.
 */
export function checkNewsletterBody(
  key: string,
  html: string,
  assetsBaseUrl: string,
): ReadonlyArray<NewsletterBodyProblem> {
  const found = new Set<string>();
  const add = (problem: string): void => {
    found.add(problem);
  };
  const bytes = new TextEncoder().encode(html).length;
  if (bytes > NEWSLETTER_BODY_MAX_BYTES) {
    add(`is ${bytes} bytes; the limit is ${NEWSLETTER_BODY_MAX_BYTES}`);
  }

  const seen = new Set<string>();
  for (const m of html.matchAll(TOKEN_RE)) {
    const name = m[1] ?? "";
    if (!(NEWSLETTER_TOKENS as ReadonlyArray<string>).includes(name)) {
      add(`carries the token {{${name}}}; only {{first_name}}, {{preview_text}} and {{unsubscribe_url}} are filled`);
    } else {
      seen.add(name);
    }
    // Right after `<` or `</` the value would be read as a tag: a member
    // named `img src=x onerror=…` would write one.
    const at = m.index ?? 0;
    if (html[at - 1] === "<" || (html[at - 1] === "/" && html[at - 2] === "<")) {
      add("puts a {{token}} where a tag name goes");
    }
  }
  // Only a leftover `{{` is a broken token: `}}` legitimately closes a CSS
  // `@media` block in the head.
  if (html.replace(TOKEN_RE, "").includes("{{")) {
    add("has a malformed {{token}}");
  }
  for (const t of NEWSLETTER_REQUIRED_TOKENS) {
    if (!seen.has(t)) add(`has no {{${t}}}`);
  }

  const assetPrefix = `${assetsBaseUrl.replace(/\/+$/u, "")}/`;
  const fromAssets = (url: string): boolean =>
    url.slice(0, assetPrefix.length).toLowerCase() === assetPrefix.toLowerCase();

  const scan = scanHtml(html);
  for (const problem of scan.problems) add(problem);

  for (const tag of scan.tags) {
    if (!NEWSLETTER_TAGS.has(tag.name) && !(tag.inComment && isOutlookTag(tag.name))) {
      add(`contains a <${tag.name}> tag`);
      continue;
    }
    if (tag.closing) continue;
    const names = new Set<string>();
    for (const attr of tag.attributes) {
      const name = attr.name;
      const raw = attr.value ?? "";
      if (names.has(name)) {
        // `<a href="https://…" href="{{unsubscribe_url}}">` links to the first.
        add(`repeats the ${name} attribute on a <${tag.name}> tag`);
        continue;
      }
      names.add(name);
      if (name.includes("{{") || name.includes("}}")) {
        add(`has a {{token}} inside a <${tag.name}> tag`);
        continue;
      }
      if (name.startsWith("on")) {
        add(`has an event handler (${name}) on a <${tag.name}> tag`);
        continue;
      }
      if (!attr.quoted && raw.includes("{{")) {
        // Unquoted, a space in the filled value would start a new attribute.
        add(`fills a {{token}} into an unquoted <${tag.name}> ${name}; quote the value`);
        continue;
      }
      const { text: decoded, unknownNamed } = decodeHtmlReferences(raw);
      const unreadable = `has a character reference this check cannot read in a <${tag.name}> ${name}`;

      if (name === "style") {
        if (raw.includes("{{")) add(`fills a {{token}} into a <${tag.name}> style attribute`);
        if (unknownNamed) add(unreadable);
        for (const p of cssProblems(decoded, `a <${tag.name}> style attribute`, assetPrefix, fromAssets)) add(p);
        continue;
      }

      if (IMAGE_ATTRIBUTES.has(name)) {
        if (raw.includes("{{")) {
          add(`fills a {{token}} into a picture's address (<${tag.name}> ${name})`);
          continue;
        }
        if (unknownNamed) {
          add(unreadable);
          continue;
        }
        for (const candidate of name === "srcset" ? srcsetUrls(decoded) : [decoded]) {
          const url = normalizeUrlValue(candidate);
          if (url.length > 0 && !fromAssets(url)) {
            add(`shows an image from outside ${assetPrefix}: ${url.slice(0, 120)}`);
          }
        }
        continue;
      }

      if (name === "href") {
        const url = normalizeUrlValue(decoded);
        // An unsubscribe link, counted after this loop (`countLinks`).
        if (UNSUBSCRIBE_HREF_RE.test(url)) continue;
        if (raw.includes("{{")) {
          add("fills a {{token}} into a link; only {{unsubscribe_url}} is filled there, as the whole href");
          continue;
        }
        if (unknownNamed) {
          add(unreadable);
          continue;
        }
        const lower = url.toLowerCase();
        if (lower.startsWith("javascript:")) {
          add("contains a javascript: URL");
        } else if (!/^https:\/\/\S+$/u.test(lower) && !/^mailto:\S+$/u.test(lower)) {
          add(`links to something that is not https or mailto: ${url.slice(0, 120)}`);
        }
        continue;
      }

      if (OTHER_URL_ATTRIBUTES.has(name)) {
        add(`has a ${name} address on a <${tag.name}> tag, which a newsletter does not use`);
        continue;
      }
      if (tag.name === "meta" && name === "http-equiv") {
        const value = decoded.trim().toLowerCase();
        if (!META_HTTP_EQUIV.has(value)) add(`contains a <meta http-equiv=${value.slice(0, 40)}> tag`);
      }
    }
  }

  for (const block of scan.styleBlocks) {
    if (block.css.includes("{{")) add("fills a {{token}} into a <style> block");
    for (const p of cssProblems(block.css, "a <style> block", assetPrefix, fromAssets)) add(p);
  }

  if (seen.has("unsubscribe_url")) {
    const unsubscribe = countLinks(scan.tags, (url) => UNSUBSCRIBE_HREF_RE.test(url));
    if (unsubscribe.links === 0) {
      add('has no unsubscribe link: an <a href="{{unsubscribe_url}}"> outside any comment');
    } else if (unsubscribe.clickable === 0) {
      add('has an unsubscribe link with nothing to click: put its words inside the <a href="{{unsubscribe_url}}">');
    }
  }
  return [...found].map((problem) => ({ body: key, problem }));
}

/**
 * Every problem with an edition's set of bodies: each key well-formed, each
 * body clean, and a `<lifecycle>:general` body for every lifecycle the
 * edition goes to (every goal falls back to it).
 */
export function checkNewsletterBodies(
  bodies: Readonly<Record<string, string>>,
  lifecycles: ReadonlyArray<MarketingLifecycle>,
  assetsBaseUrl: string,
): ReadonlyArray<NewsletterBodyProblem> {
  const problems: NewsletterBodyProblem[] = [];
  const keyRe = new RegExp(NEWSLETTER_BODY_KEY_PATTERN, "u");
  const lifecycleSet = new Set<string>(lifecycles);
  for (const [key, html] of Object.entries(bodies)) {
    if (!keyRe.test(key)) {
      problems.push({ body: key, problem: "is not a lifecycle:goal key" });
      continue;
    }
    const lifecycle = key.split(":")[0] ?? "";
    if (!lifecycleSet.has(lifecycle)) {
      problems.push({ body: key, problem: `is for ${lifecycle}, which this edition does not go to` });
    }
    problems.push(...checkNewsletterBody(key, html, assetsBaseUrl));
  }
  for (const lifecycle of lifecycles) {
    if (!Object.prototype.hasOwnProperty.call(bodies, `${lifecycle}:general`)) {
      problems.push({ body: `${lifecycle}:general`, problem: "is missing; every goal falls back to it" });
    }
  }
  return problems;
}

/** HTML-escape a value before it is written into a body. */
export function escapeNewsletterValue(value: string): string {
  return value
    .replace(/&/gu, "&amp;")
    .replace(/</gu, "&lt;")
    .replace(/>/gu, "&gt;")
    .replace(/"/gu, "&quot;")
    .replace(/'/gu, "&#39;");
}

/**
 * Fill the three tokens. Each value is HTML-escaped, so a name can never
 * become markup and a URL's `&` is valid inside an `href`. A body that
 * passed {@link checkNewsletterBody} has no other token left to fill.
 */
export function fillNewsletterBody(
  html: string,
  values: Readonly<Record<NewsletterToken, string>>,
): string {
  return html.replace(TOKEN_RE, (whole, name: string | undefined) => {
    if (name !== undefined && (NEWSLETTER_TOKENS as ReadonlyArray<string>).includes(name)) {
      return escapeNewsletterValue(values[name as NewsletterToken]);
    }
    return whole;
  });
}

// ---------------------------------------------------------------------------
// The From line.
// ---------------------------------------------------------------------------

/**
 * Parse `E-Pulsive <hello@e-pulsive.co.uk>` or a bare address. `null` when it
 * is neither, or the display name holds characters that would need quoting
 * (quotes, brackets, commas, line breaks).
 */
export function parseNewsletterFrom(
  raw: string,
): { readonly name: string | null; readonly address: string } | null {
  const s = raw.trim();
  const bracketed = /^([^<>"\r\n,;:@]{1,64}?)\s*<([^<>\s@]+@[^<>\s@]+)>$/u.exec(s);
  if (bracketed !== null) {
    const name = (bracketed[1] ?? "").trim();
    const address = (bracketed[2] ?? "").trim();
    if (name.length === 0 || name.length > NEWSLETTER_FROM_NAME_MAX) return null;
    return { name, address };
  }
  if (/^[^<>\s@"]+@[^<>\s@"]+$/u.test(s)) return { name: null, address: s };
  return null;
}

// ---------------------------------------------------------------------------
// The A/B split.
// ---------------------------------------------------------------------------

/**
 * cyrb53 — a small, fast, well-mixed 53-bit string hash. Not cryptographic;
 * it only has to shuffle members evenly and the same way every time, in
 * Node and in the browser alike.
 */
export function newsletterHash53(text: string): number {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Split one audience into arms A and B, balanced to within one member: the
 * members are ordered by a hash of the edition and their id, then dealt
 * A, B, A, B. Deterministic — the same edition and members always split the
 * same way — and independent of the order the ids arrive in. Called once
 * per lifecycle, so every lifecycle is balanced on its own.
 */
export function assignNewsletterVariants(
  editionId: string,
  memberIds: ReadonlyArray<string>,
): ReadonlyMap<string, NewsletterVariant> {
  const ordered = [...new Set(memberIds)]
    .map((id) => ({ id, h: newsletterHash53(`${editionId}:${id}`) }))
    .sort((a, b) => a.h - b.h || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const out = new Map<string, NewsletterVariant>();
  ordered.forEach((m, i) => {
    out.set(m.id, i % 2 === 0 ? "A" : "B");
  });
  return out;
}

// ---------------------------------------------------------------------------
// One edition's audience.
// ---------------------------------------------------------------------------

/** One recipient of an edition: their lifecycle, goal, arm and body. */
export interface NewsletterRecipientPlan {
  readonly memberId: string;
  readonly lifecycle: MarketingLifecycle;
  readonly goal: MarketingGoal;
  readonly variant: NewsletterVariant;
  /** The body they get. */
  readonly bodyKey: string;
}

export interface NewsletterVariantCounts {
  readonly A: number;
  readonly B: number;
}

export interface NewsletterGoalCount {
  readonly goal: MarketingGoal;
  readonly recipients: number;
  readonly variants: NewsletterVariantCounts;
  readonly bodyKey: string | null;
}

export interface NewsletterLifecycleCount {
  readonly lifecycle: MarketingLifecycle;
  readonly recipients: number;
  readonly variants: NewsletterVariantCounts;
  readonly goals: ReadonlyArray<NewsletterGoalCount>;
}

/** Who one edition goes to: the plan the preview counts and the send freezes. */
export interface NewsletterAudiencePlan {
  readonly recipients: ReadonlyArray<NewsletterRecipientPlan>;
  readonly lifecycles: ReadonlyArray<NewsletterLifecycleCount>;
  readonly total: number;
  /** Members in the edition's lifecycles whom email cannot reach, by reason. */
  readonly excluded: Readonly<Record<MarketingExclusionReason, number>>;
  /** Active members in a lifecycle the edition does not go to. */
  readonly outsideLifecycles: number;
  /**
   * Reachable members left out because the edition has no body for their
   * lifecycle — never, for an edition that passed its checks. Left out
   * before the arms are dealt, so they cannot unbalance them.
   */
  readonly withoutBody: number;
}

/**
 * The edition's audience from the classified members: everyone in one of its
 * lifecycles whom email can reach and the edition has a body for, split into
 * arms A and B inside each lifecycle ({@link assignNewsletterVariants}), each
 * given the body for their goal or its fallback ({@link pickNewsletterBodyKey}).
 * Every member is counted once: `total + Σ excluded + outsideLifecycles +
 * withoutBody` is the number of entries. The preview shows these counts; the
 * send freezes these recipients, so the number approved is the number sent to.
 */
export function planNewsletterAudience(
  entries: ReadonlyArray<MarketingAudienceEntry>,
  edition: {
    readonly editionId: string;
    readonly lifecycles: ReadonlyArray<MarketingLifecycle>;
    readonly bodyKeys: ReadonlyArray<string>;
  },
): NewsletterAudiencePlan {
  const lifecycleSet = new Set<MarketingLifecycle>(edition.lifecycles);
  const bodyKeys = new Set(edition.bodyKeys);
  const excluded = emptyExclusionCounts();
  const byLifecycle = new Map<MarketingLifecycle, MarketingAudienceEntry[]>();
  let outsideLifecycles = 0;
  for (const e of entries) {
    if (!lifecycleSet.has(e.lifecycle)) {
      outsideLifecycles += 1;
      continue;
    }
    if (!e.email.reachable) {
      excluded[e.email.reason] += 1;
      continue;
    }
    const list = byLifecycle.get(e.lifecycle) ?? [];
    list.push(e);
    byLifecycle.set(e.lifecycle, list);
  }

  const recipients: NewsletterRecipientPlan[] = [];
  const lifecycles: NewsletterLifecycleCount[] = [];
  let withoutBody = 0;
  for (const lifecycle of MARKETING_LIFECYCLES) {
    if (!lifecycleSet.has(lifecycle)) continue;
    const members: Array<{ readonly memberId: string; readonly goal: MarketingGoal; readonly bodyKey: string }> = [];
    for (const m of byLifecycle.get(lifecycle) ?? []) {
      const bodyKey = pickNewsletterBodyKey(bodyKeys, lifecycle, m.goal);
      if (bodyKey === null) withoutBody += 1;
      else members.push({ memberId: m.memberId, goal: m.goal, bodyKey });
    }
    const arms = assignNewsletterVariants(
      edition.editionId,
      members.map((m) => m.memberId),
    );
    const goalCounts = new Map<MarketingGoal, { n: number; a: number; b: number }>();
    let a = 0;
    let b = 0;
    for (const m of members) {
      const variant = arms.get(m.memberId) ?? "A";
      recipients.push({ memberId: m.memberId, lifecycle, goal: m.goal, variant, bodyKey: m.bodyKey });
      const g = goalCounts.get(m.goal) ?? { n: 0, a: 0, b: 0 };
      g.n += 1;
      if (variant === "A") {
        g.a += 1;
        a += 1;
      } else {
        g.b += 1;
        b += 1;
      }
      goalCounts.set(m.goal, g);
    }
    lifecycles.push({
      lifecycle,
      recipients: members.length,
      variants: { A: a, B: b },
      goals: MARKETING_GOALS.map((goal) => {
        const g = goalCounts.get(goal) ?? { n: 0, a: 0, b: 0 };
        return {
          goal,
          recipients: g.n,
          variants: { A: g.a, B: g.b },
          bodyKey: pickNewsletterBodyKey(bodyKeys, lifecycle, goal),
        };
      }),
    });
  }
  return {
    recipients,
    lifecycles,
    total: recipients.length,
    excluded,
    outsideLifecycles,
    withoutBody,
  };
}

/** The lifecycles an edition goes to when it names none. */
export const NEWSLETTER_DEFAULT_LIFECYCLES: ReadonlyArray<MarketingLifecycle> = MARKETING_LIFECYCLES;

/** Every goal, for forms and tables. */
export const NEWSLETTER_GOALS: ReadonlyArray<MarketingGoal> = MARKETING_GOALS;
